/* eslint-disable jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events */

import React from 'react';
import PropTypes from 'prop-types';

import { bemGenerator } from '@prisjakt/ui-base';
import MenuUp from 'mdi-react/MenuUpIcon';
import MenuDown from 'mdi-react/MenuDownIcon';
import T from '@prisjakt/ui-translate';
import Tooltip from '@prisjakt/ui-tooltip';

import './index.scss';

const styles = bemGenerator('pj-ui-pl-header', ['cell', 'arrow']);

const truncate = (maxLength, text) =>
  text.length > maxLength ? `${text.substr(0, maxLength)}…` : text;

const getSortIcon = (sortInfo, name) =>
  sortInfo && sortInfo.name === name && sortInfo.direction === 'asc' ? (
    <MenuUp className={styles.arrow} size={10} viewBox="6 6 12 12" />
  ) : (
    <MenuDown className={styles.arrow} size={10} viewBox="6 6 12 12" />
  );

const isPriceSortMode = sortInfo => /^price/.test(sortInfo.name);
const getPriceSortMode = sortInfo => isPriceSortMode(sortInfo) && sortInfo.name;
const getPriceColumnLabel = sortInfo => {
  const defaultLabel = (
    <T domain="category">product-list.price-column-heading</T>
  );
  switch (getPriceSortMode(sortInfo)) {
    case 'price':
      return defaultLabel;
    case 'price_include_shipping':
      return (
        <T domain="category">product-list.price-shipping-column-heading</T>
      );
    default:
      return defaultLabel;
  }
};

const ProductListingHeader = ({
  selectedProperties,
  onSortChange,
  sortInfo,
  firstColumnIndent,
}) => (
  <div className={styles.block}>
    {firstColumnIndent > 0 &&
      Array(firstColumnIndent).fill(<div className={styles.cell} />)}
    <a
      className={styles.cell.withModifier({
        selected: sortInfo.name === 'name',
      })}
      role="link"
      tabIndex="0"
      onClick={() => onSortChange('name')}
    >
      <T domain="category">product-list.name-column-heading</T>
      {getSortIcon(sortInfo, 'name')}
    </a>
    <a
      className={styles.cell.withModifier({
        right: true,
        selected: sortInfo.name === getPriceSortMode(sortInfo),
      })}
      role="link"
      tabIndex="0"
      onClick={() => onSortChange(getPriceSortMode(sortInfo) || 'price')}
    >
      {getPriceColumnLabel(sortInfo)}
      {getSortIcon(sortInfo, getPriceSortMode(sortInfo))}
    </a>
    {selectedProperties &&
      selectedProperties
        .map(prop => ({
          ...prop,
          // Truncate to arbitrary length to prevent column headers from growing too tall.
          truncatedName: truncate(20, prop.name),
        }))
        .map(prop => {
          const propertyName = `property:${prop.id}`;
          const isPropertySelected = sortInfo.name === propertyName;
          const shouldTruncateName =
            selectedProperties.length > 2 && prop.name !== prop.truncatedName;
          return (
            <a
              key={prop.id}
              className={styles.cell.withModifier({
                narrow: selectedProperties.length === 2,
                narrower: selectedProperties.length === 3,
                narrowest: selectedProperties.length >= 4,
                selected: isPropertySelected,
              })}
              aria-label={shouldTruncateName ? prop.name : null}
              role="link"
              tabIndex={0}
              onClick={() => onSortChange(propertyName)}
            >
              {shouldTruncateName ? prop.truncatedName : prop.name}
              {getSortIcon(sortInfo, propertyName)}
              {shouldTruncateName && (
                <Tooltip position="left">{prop.name}</Tooltip>
              )}
            </a>
          );
        })}
  </div>
);

const { arrayOf, shape, string } = PropTypes;

ProductListingHeader.propTypes = {
  selectedProperties: arrayOf(
    shape({
      id: string,
    })
  ),
  onSortChange: PropTypes.func,
  sortInfo: PropTypes.object,
  firstColumnIndent: PropTypes.number,
};

ProductListingHeader.defaultProps = {
  selectedProperties: null,
  onSortChange: () => {},
  sortInfo: {},
  firstColumnIndent: 0,
};

export default ProductListingHeader;
